import LengthValue from '$src/CGS/domain/dto/core/Length/LengthValue';
import * as THREE from 'three';
import { EMaterialSlot } from '../../Meshes/EMaterialSlot';
import { SubMesh } from '../../Meshes/SubMesh';
import { SubMeshCollection } from '../../Meshes/SubMeshCollection';
import { CabinetObject } from '../Objects/CabinetObject';
import { ICabinetShapeGenerator } from './ICabinetShapeGenerator';
import EItemTypeExt from '$src/CGS/domain/dto/core/EItemTypeExt';
import { PlinthObject } from '../Objects/PlinthObject';
import { LengthMaths } from '$src/CGS/domain/dto/core/Length/LengthMaths';

export class PlinthGenerator {
    private _cabinetShapeGenerator: ICabinetShapeGenerator;
    private _subMeshCollection: SubMeshCollection = new SubMeshCollection();

    constructor(cabinetShapeGenerator: ICabinetShapeGenerator) {
        this._cabinetShapeGenerator = cabinetShapeGenerator;
    }

    generate(cabinet: CabinetObject): PlinthObject | null {
        if ( EItemTypeExt.isWallCabinet(this._cabinetShapeGenerator.cabinet.Type)) {
            return null;
        }

        const insideOutline = this._cabinetShapeGenerator.getInsideOutline(this._cabinetShapeGenerator.cabinet.Constants.PlinthDepth, LengthMaths.Zero);

        cabinet.Plinth = new PlinthObject();
        cabinet.Placement.add(cabinet.Plinth);

        this.generateOutline(insideOutline, this._cabinetShapeGenerator.cabinet.Constants.PlinthHeight);
        cabinet.Plinth.MeshInfo = this._subMeshCollection.createMesh(cabinet.Plinth);

        return cabinet.Plinth;
    }

    private generateOutline(outline: THREE.Vector3[], plinthHeight: LengthValue) {
        for (let i = 0; i < outline.length; i++) {
            const firstCoordinate = outline[i];
            const secondCoordinate = outline[(i + 1) % outline.length];

            let subMesh = this.generateSingleFace(firstCoordinate, secondCoordinate, plinthHeight, false);
            this._subMeshCollection.add(subMesh, EMaterialSlot.Plinth);
        }
    }

    private generateSingleFace(begin: THREE.Vector3, end: THREE.Vector3, plinthHeight: LengthValue, outline: boolean): SubMesh {
        const vertices: THREE.Vector3[] = [
            begin,
            end,
            new THREE.Vector3(end.x, end.y - plinthHeight.meter, end.z),
            new THREE.Vector3(begin.x, begin.y - plinthHeight.meter, begin.z),
        ];

        return outline ? SubMesh.outline(true, ...vertices) : SubMesh.polygon(...vertices);
    }

    generateUnderVerticalGroove(anchor: THREE.Vector3, direction: THREE.Vector3, isLeft: boolean) {
        const backDirection = new THREE.Vector3().copy(direction)
                .applyAxisAngle(new THREE.Vector3(0, 1, 0), isLeft ? Math.PI / 2 : -Math.PI / 2)
                .normalize();

        const plinthStart = anchor.clone().addScaledVector(direction, this._cabinetShapeGenerator.cabinet.Constants.BoxThickness.meter)
            .addScaledVector(backDirection, this._cabinetShapeGenerator.cabinet.Constants.PlinthDepth.meter);

        const plinthEnd = anchor.clone()
            .addScaledVector(direction, this._cabinetShapeGenerator.cabinet.Constants.GrooveWidth.meter)
            .addScaledVector(backDirection, this._cabinetShapeGenerator.cabinet.Constants.PlinthDepth.meter);

        const plinthHeight = this._cabinetShapeGenerator.cabinet.Constants.PlinthDepth;

        const vertices: THREE.Vector3[] = [
            plinthStart,
            plinthEnd,
            new THREE.Vector3(plinthStart.x, plinthStart.y - plinthHeight.meter, plinthStart.z),
            new THREE.Vector3(plinthEnd.x, plinthEnd.y - plinthHeight.meter, plinthEnd.z),
        ];

        const indices = isLeft 
            ? [0, 2, 1, 1, 2, 3]
            : [0, 1, 2, 2, 1, 3];

        const subMesh = new SubMesh(vertices, indices);
        this._subMeshCollection.add(subMesh, EMaterialSlot.Plinth);
    }
}
